import { publicAssetUrl } from "@/lib/publicAssetUrl";

export type BetaModule = {
  id: string;
  name: string;
  label: string;
  tease: string;
  color: string;
  /** Public URL e.g. /images/modules/guardians-eye.png */
  image: string | null;
  /** Public URL e.g. /images/ui/launcher/guardians-eye.png */
  icon: string | null;
};

/** Launcher icon path under public/images/ui/launcher/ */
export function launcherIconPath(moduleId: string): string {
  return publicAssetUrl(`images/ui/launcher/${moduleId}.png`);
}

/** Module screenshot from screengrabs/ (copied to public/images/modules/) */
export function moduleScreenshotPath(moduleId: string): string {
  return publicAssetUrl(`images/modules/${moduleId}.png`);
}

type BetaModuleDefinition = Omit<BetaModule, "icon" | "image"> & {
  /** Show in scroll icon field / hurricane (default true) */
  iconField?: boolean;
};

/**
 * Beta showcase modules — 15 tiles (Dashboard + 14 launcher modules; excludes Advent Chronicle & Mini Games).
 * When merged into monorepo, derive from MODULE_REGISTRY in shared/config/modules.ts.
 */
const BETA_MODULE_DEFINITIONS: BetaModuleDefinition[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    label: "Dashboard",
    tease: "One launcher. Every session tool wired together — combat, maps, audio, reveals, and a library built from the PDFs you already own.",
    color: "#fbbf24",
    iconField: false,
  },
  {
    id: "campaign",
    name: "Campaign Manager",
    label: "Campaigns",
    tease: "Create campaigns, send invites, and keep your party sheet in one place.",
    color: "#4A90D9",
  },
  {
    id: "guardians-eye",
    name: "Guardian's Eye",
    label: "Command",
    tease: "Your GM control center — orchestrate every tool without losing the room.",
    color: "#3388FF",
  },
  {
    id: "heros-loom",
    name: "The Hero's Loom",
    label: "Players",
    tease: "Player hub and character tools — sheets, story, and connection between sessions.",
    color: "#6366F1",
  },
  {
    id: "adventurers-eye",
    name: "The Adventurer's Eye",
    label: "Reveals",
    tease: "Cinematic visual reveals at the table — not a URL lost in chat.",
    color: "#FFB84D",
  },
  {
    id: "wardens-vanguard",
    name: "The Warden's Vanguard",
    label: "Combat",
    tease: "Initiative and combat management — turns, HP, and conditions in rhythm with the story.",
    color: "#FF4444",
  },
  {
    id: "visbys-portal",
    name: "Visby's Portal",
    label: "Maps",
    tease: "Tactical display and live play — fog, tokens, and every eye on the same map.",
    color: "#E6A84D",
  },
  {
    id: "wardens-echo",
    name: "Warden's Echo",
    label: "Audio",
    tease: "Audio and ambience on your cue — tension, atmosphere, and silence when it counts.",
    color: "#66FF77",
  },
  {
    id: "astral-stream",
    name: "The Astral Stream",
    label: "Broadcast",
    tease: "Broadcast handouts, slideshows, and Known Entities to the whole table.",
    color: "#AA44FF",
  },
  {
    id: "encounter-crucible",
    name: "Encounter Crucible",
    label: "Encounters",
    tease: "Build balanced encounters and load them straight into combat night.",
    color: "#FF7A4D",
  },
  {
    id: "realm-weaver",
    name: "Realm Weaver",
    label: "Cartography",
    tease: "Map management and creation — upload, calibrate, and prep vision before session.",
    color: "#E6A84D",
  },
  {
    id: "token-forge",
    name: "Token Forge",
    label: "Tokens",
    tease: "Create and manage token art — frames, rings, and exports for the Omnarium.",
    color: "#FF9A4D",
  },
  {
    id: "content-intelligence",
    name: "Content Intelligence",
    label: "Extract",
    tease: "PDF extraction and analysis — turn books you own into structured, runnable content.",
    color: "#00AAFF",
  },
  {
    id: "omnarium",
    name: "Omnarium",
    label: "Library",
    tease: "Browse and manage extracted content — monsters, spells, maps, and everything you've imported.",
    color: "#D4AF37",
  },
  {
    id: "the-grimoire",
    name: "The GriMoire",
    label: "Prep",
    tease: "GM prep workspace — notes, canvas, codex graph, and session river in one arcane desk.",
    color: "#C026D3",
  },
];

export const BETA_MODULES: BetaModule[] = BETA_MODULE_DEFINITIONS.map((mod) => ({
  ...mod,
  image: moduleScreenshotPath(mod.id),
  icon: launcherIconPath(mod.id),
}));

/** Launcher modules only — excludes Dashboard from hurricane / dock icons */
export const ICON_FIELD_MODULES: BetaModule[] = BETA_MODULES.filter(
  (mod) => BETA_MODULE_DEFINITIONS.find((d) => d.id === mod.id)?.iconField !== false
);
